/**
 * VoiceAgentCallPanel — renders an AI voice-agent call's transcript + assertions
 * on the single-run detail page. Data comes straight from the already-streamed
 * execution doc (`result.outputs.agentResult`) — no extra endpoint or listener.
 */

import React from 'react';
import { Bot, CheckCircle2, XCircle } from 'lucide-react';
import { Card, Badge } from '@/components/ui';
import { useUserTimezone } from '@/hooks/useDateFormat';

interface AgentAssertion {
  type?: string;
  expr?: string;
  pass?: boolean;
}
interface AgentTurn {
  role?: string;
  text?: string;
  /** Elapsed seconds since call start. */
  t?: number;
}
interface AgentResult {
  status?: string;
  passed?: boolean;
  endReason?: string;
  turns?: number;
  dtmfSent?: string;
  /** DTMF the FAR SIDE pressed, decoded from the call audio. */
  dtmfDetected?: string;
  /** The same digits with when each arrived (offset into the call + instant). */
  dtmfDetectedEvents?: Array<{ digit?: string; atMs?: number; at?: string; source?: string }>;
  connectedSec?: number;
  /** Post-dial delay (ms): destination dial → answer (#223). Connected calls only. */
  dialToAnswerMs?: number | null;
  /** Total connection latency (ms): call attempt start → answer. Connected calls only. */
  connectionTimeMs?: number | null;
  assertions?: AgentAssertion[];
  transcript?: AgentTurn[];
  /** External-outcome classification when the call didn't connect. */
  outcome?: string | null;
  outcomeLabel?: string | null;
  outcomeReason?: string | null;
}

/** Pull the agent result off an execution doc, if present. */
export function getAgentResult(execution: {
  result?: { outputs?: Record<string, any> };
  outputs?: Record<string, any>;
} | null | undefined): AgentResult | null {
  const ar = execution?.result?.outputs?.agentResult ?? execution?.outputs?.agentResult;
  return ar && typeof ar === 'object' ? (ar as AgentResult) : null;
}

/** A→B header line, when the numbers are on the doc outputs. */
function callerCallee(execution: {
  result?: { outputs?: Record<string, any> };
  outputs?: Record<string, any>;
}): { a: string | null; b: string | null } {
  const out = execution.result?.outputs ?? execution.outputs ?? {};
  const a = typeof out.callerMsisdn === 'string' && out.callerMsisdn ? out.callerMsisdn : null;
  const b = typeof out.phoneNumber === 'string' && out.phoneNumber ? out.phoneNumber : null;
  return { a, b };
}

/** Firestore Timestamp | ISO string | Date → ms since epoch, or null. */
function toEpochMs(v: { toDate?: () => Date } | string | Date | undefined | null): number | null {
  if (!v) return null;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? null : t; }
  if (v instanceof Date) return v.getTime();
  if (typeof v.toDate === 'function') { try { return v.toDate().getTime(); } catch { return null; } }
  return null;
}
const clock = (ms: number, timeZone: string) =>
  new Date(ms).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit', timeZone });

export default function VoiceAgentCallPanel({
  execution,
}: {
  execution: {
    result?: { outputs?: Record<string, any> };
    outputs?: Record<string, any>;
    startedAt?: { toDate?: () => Date } | string | Date;
  };
}) {
  const timeZone = useUserTimezone();
  const agent = getAgentResult(execution);
  if (!agent || (!agent.transcript?.length && !agent.assertions?.length)) return null;
  const { a, b } = callerCallee(execution);

  // Actual clock times of the dial + connect events (the run start + the offsets
  // the agent recorded). `connectionTimeMs` = start → answer; the dial completed
  // `dialToAnswerMs` before the answer. Absolute time, not a relative duration.
  const startMs = toEpochMs(execution.startedAt);
  const connectedAtMs =
    startMs != null && typeof agent.connectionTimeMs === 'number' ? startMs + agent.connectionTimeMs : null;
  const dialedAtMs =
    connectedAtMs != null && typeof agent.dialToAnswerMs === 'number'
      ? connectedAtMs - agent.dialToAnswerMs : null;

  return (
    <Card padding="lg">
      <div className="flex items-center gap-2 mb-4">
        <Bot className="h-5 w-5 text-muted-foreground" />
        <h2 className="text-h3 font-semibold text-foreground">AI agent call</h2>
        <Badge variant={agent.passed ? 'success' : 'error'} className="ml-1">
          {agent.passed ? 'Passed' : 'Failed'}
        </Badge>
        {/* External outcome (busy / no answer / rejected): the call reached the
            far end but didn't connect — not a stack failure. */}
        {!agent.passed && agent.outcomeLabel ? (
          <span className="ml-1 inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-600 dark:text-amber-500">
            {agent.outcomeLabel}
          </span>
        ) : null}
      </div>
      {/* Human reason, when the call didn't connect for an external cause. */}
      {!agent.passed && agent.outcomeReason ? (
        <p className="mb-4 text-sm text-muted-foreground">{agent.outcomeReason}</p>
      ) : null}

      {/* Summary chips */}
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground mb-4">
        {a || b ? (
          <span>
            <span className="font-mono text-foreground">{a ?? '—'}</span>
            <span className="mx-1.5">→</span>
            <span className="font-mono text-foreground">{b ?? '—'}</span>
          </span>
        ) : null}
        {agent.endReason ? <span>End: <span className="text-foreground">{agent.endReason}</span></span> : null}
        {typeof agent.turns === 'number' ? <span>{agent.turns} turns</span> : null}
        {dialedAtMs != null ? <span>Dialed: <span className="text-foreground">{clock(dialedAtMs, timeZone)}</span></span> : null}
        {connectedAtMs != null ? <span>Connected: <span className="text-foreground">{clock(connectedAtMs, timeZone)}</span></span> : null}
        {typeof agent.connectedSec === 'number' ? <span>{agent.connectedSec.toFixed(0)}s connected</span> : null}
        {agent.dtmfSent ? <span>DTMF sent: <span className="font-mono text-foreground">{agent.dtmfSent}</span></span> : null}
        {agent.dtmfDetected ? (
          <span>
            DTMF heard:{' '}
            <span className="font-mono text-foreground">
              {/* Each key with how far into the call it landed — a bare digit
                  string can't tell "they keyed it while we waited" from "they
                  keyed it after the answer". */}
              {agent.dtmfDetectedEvents?.length
                ? agent.dtmfDetectedEvents
                    .map((e) => (typeof e.atMs === 'number' ? `${e.digit ?? '?'}@${(e.atMs / 1000).toFixed(1)}s` : e.digit ?? '?'))
                    .join(' ')
                : agent.dtmfDetected}
            </span>
          </span>
        ) : null}
      </div>

      {/* Assertions */}
      {agent.assertions && agent.assertions.length > 0 ? (
        <div className="mb-4 space-y-1.5">
          {agent.assertions.map((as, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {as.pass
                ? <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                : <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
              <span className="font-medium">{as.type ?? 'assertion'}</span>
              {as.expr ? <span className="font-mono text-xs text-muted-foreground truncate">{as.expr}</span> : null}
              <Badge variant={as.pass ? 'success' : 'error'} className="ml-auto text-[10px] py-0">
                {as.pass ? 'PASS' : 'FAIL'}
              </Badge>
            </div>
          ))}
        </div>
      ) : null}

      {/* Transcript */}
      {agent.transcript && agent.transcript.length > 0 ? (
        <div className="max-h-96 overflow-y-auto space-y-2 rounded-lg border border-border bg-muted/20 p-3">
          {agent.transcript.map((t, i) => {
            const isAgent = t.role === 'agent';
            return (
              <div key={i} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[80%] rounded-lg px-3 py-1.5 text-sm ${
                    isAgent ? 'bg-primary/10 text-foreground' : 'bg-muted text-foreground'
                  }`}
                >
                  <div className="mb-0.5 flex items-center justify-between gap-3 text-[10px] uppercase tracking-wide text-muted-foreground">
                    <span>{t.role ?? 'unknown'}</span>
                    {typeof t.t === 'number' && <span className="font-mono normal-case">+{t.t}s</span>}
                  </div>
                  {t.text ?? ''}
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
    </Card>
  );
}
